import React, { useMemo } from 'react';
import PropTypes from 'prop-types';
import {
  Box, Typography, Grid, Chip, Paper, Stack, Fade,
} from '@mui/material';
import { alpha, useTheme } from '@mui/material/styles';
import EmojiEventsIcon from '@mui/icons-material/EmojiEvents';
import StarIcon from '@mui/icons-material/Star';
import FitnessCenterIcon from '@mui/icons-material/FitnessCenter';
import { useResourceState } from '@platform/core/src/context/ResourceContext';
import { useResourceOpener } from '@platform/core/src/hooks/useResourceOpener';
import SectionHeader from './SectionHeader';
import DetailSection from './DetailSection';
import TrendIndicator from './TrendIndicator';
import DeltaChip from './DeltaChip';
import { GradientBackground } from './styled';

const ExercisesAndPRsDetail = ({ prSummary, exerciseAnalyses, activityReports, onBack }) => {
  const theme = useTheme();
  const { getResourceById } = useResourceState();
  const { openResource } = useResourceOpener();

  const handleViewExercise = (exerciseId) => {
    openResource(exerciseId, 'exercise_definition', { mode: 'modal' });
  };

  const getExerciseName = (item) => {
    if (item.activity_name && item.activity_name !== 'Exercise') return item.activity_name;
    const def = getResourceById(item.exercise_definition_id, 'exercise_definition');
    return def?.name || 'Unknown Exercise';
  };

  const allPRs = useMemo(() => {
    const prMap = new Map();
    if (prSummary?.notable_prs?.length > 0) {
      prSummary.notable_prs.forEach(pr => {
        const key = `${pr.exercise_name}-${pr.pr_type || 'default'}`;
        if (!prMap.has(key)) {
          prMap.set(key, pr);
        }
      });
    }
    // Fallback if PR summary is empty but individual exercises have PR flags
    if (prMap.size === 0) {
      const rawExercises = exerciseAnalyses?.length > 0 ? exerciseAnalyses : activityReports;
      rawExercises?.filter(ex => ex.is_all_time_pr || ex.is_session_pr || ex.is_personal_record)?.forEach(ex => {
        const exerciseName = ex.activity_name || ex.exercise_name || 'Unknown';
        const key = `${exerciseName}-${ex.pr_types?.[0] || 'default'}`;
        if (!prMap.has(key)) {
          prMap.set(key, {
            exercise_name: exerciseName,
            is_all_time: ex.is_all_time_pr,
            new_value: ex.max_weight_used || ex.estimated_1rm || ex.total_volume_load,
            unit: ex.weight_unit || 'lbs',
            pr_type: ex.pr_types?.[0],
          });
        }
      });
    }
    return Array.from(prMap.values());
  }, [prSummary, exerciseAnalyses, activityReports]);

  const totalPRs = allPRs.length;
  const allTimePRs = allPRs.filter(p => p.is_all_time).length;

  const exercises = useMemo(() => {
    const rawExercises = exerciseAnalyses?.length > 0 ? exerciseAnalyses : activityReports;
    if (!rawExercises?.length) return [];
    
    // Group by exercise definition to handle multiple occurrences
    const grouped = rawExercises.reduce((acc, exercise) => {
      const key = exercise.exercise_definition_id || getExerciseName(exercise);
      const setsCount = exercise.total_sets_count || exercise.set_count || exercise.sets?.length || 0;
      const workingSetsCount = exercise.working_sets_count || exercise.sets?.filter(s => s.is_working_set)?.length || setsCount;
      
      if (!acc[key]) {
        acc[key] = {
          ...exercise,
          activity_name: getExerciseName(exercise),
          total_sets_count: setsCount,
          working_sets_count: workingSetsCount,
          total_reps: exercise.total_reps || exercise.total_working_reps || 0,
          total_volume_load: exercise.total_volume_load || 0,
          max_weight_used: exercise.max_weight_used || 0,
          estimated_1rm: exercise.estimated_1rm || 0,
          total_distance_meters: exercise.total_distance_meters || 0,
          total_duration_seconds: exercise.total_duration_seconds || 0,
          is_personal_record: exercise.is_personal_record || exercise.is_session_pr || exercise.is_all_time_pr,
        };
      } else {
        const existing = acc[key];
        existing.total_sets_count += setsCount;
        existing.working_sets_count += workingSetsCount;
        existing.total_reps += (exercise.total_reps || exercise.total_working_reps || 0);
        existing.total_volume_load += (exercise.total_volume_load || 0);
        existing.total_distance_meters += (exercise.total_distance_meters || 0);
        existing.total_duration_seconds += (exercise.total_duration_seconds || 0);
        if ((exercise.max_weight_used || 0) > existing.max_weight_used) existing.max_weight_used = exercise.max_weight_used;
        if ((exercise.estimated_1rm || 0) > existing.estimated_1rm) existing.estimated_1rm = exercise.estimated_1rm;
        if (exercise.is_personal_record || exercise.is_session_pr || exercise.is_all_time_pr) existing.is_personal_record = true;
      }
      return acc;
    }, {});
    
    return Object.values(grouped);
  }, [exerciseAnalyses, activityReports, getResourceById]);

  const totalVolume = exercises.reduce((sum, ex) => sum + (ex.total_volume_load || 0), 0);
  const totalSets = exercises.reduce((sum, ex) => sum + (ex.total_sets_count || 0), 0);

  return (
    <Fade in timeout={300}>
      <Box>
        <SectionHeader 
          icon={totalPRs > 0 ? <EmojiEventsIcon /> : <FitnessCenterIcon />} 
          title={totalPRs > 0 ? "Personal Records" : "Workout Summary"} 
          onBack={onBack} 
        />
        
        {/* Header Stats */}
        {totalPRs > 0 ? (
          <GradientBackground color="warning" sx={{ p: 3, mb: 3 }}>
            <Grid container spacing={3}>
              <Grid item xs={6}>
                <Typography variant="overline" color="text.secondary">New Records!</Typography>
                <Typography variant="h2" fontWeight={800} color="warning.main">{totalPRs}</Typography>
              </Grid>
              {allTimePRs > 0 && (
                <Grid item xs={6}>
                  <Typography variant="overline" color="text.secondary">All-Time PRs</Typography>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <StarIcon sx={{ color: 'warning.main', fontSize: 28 }} />
                    <Typography variant="h2" fontWeight={800}>{allTimePRs}</Typography>
                  </Box>
                </Grid>
              )}
            </Grid>
          </GradientBackground>
        ) : (
          <GradientBackground color="success" sx={{ p: 3, mb: 3 }}>
            <Typography variant="h5" fontWeight={700} color="success.main" gutterBottom>
              💪 Solid Work!
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Every workout builds the foundation for future PRs. You completed {exercises.length} exercise{exercises.length !== 1 ? 's' : ''} today!
            </Typography>
            <Grid container spacing={3}>
              <Grid item xs={6} sm={4}>
                <Typography variant="overline" color="text.secondary">Exercises</Typography>
                <Typography variant="h3" fontWeight={700}>{exercises.length}</Typography>
              </Grid>
              {totalSets > 0 && (
                <Grid item xs={6} sm={4}>
                  <Typography variant="overline" color="text.secondary">Total Sets</Typography>
                  <Typography variant="h3" fontWeight={700}>{totalSets}</Typography>
                </Grid>
              )}
              {totalVolume > 0 && (
                <Grid item xs={6} sm={4}>
                  <Typography variant="overline" color="text.secondary">Volume</Typography>
                  <Typography variant="h3" fontWeight={700}>{(totalVolume / 1000).toFixed(1)}k</Typography>
                </Grid>
              )}
            </Grid>
          </GradientBackground>
        )}

        {/* PR List */}
        {totalPRs > 0 && (
          <DetailSection title="Record Breakdown" icon={<EmojiEventsIcon sx={{ fontSize: 18 }} />} color="warning">
            <Stack spacing={1.5}>
              {allPRs.map((pr, idx) => (
                <Box 
                  key={idx} 
                  sx={{ 
                    p: 1.5, 
                    borderRadius: 2, 
                    bgcolor: pr.is_all_time ? alpha(theme.palette.warning.main, 0.08) : alpha(theme.palette.primary.main, 0.08),
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                    <EmojiEventsIcon sx={{ color: pr.is_all_time ? 'warning.main' : 'primary.main', fontSize: 20 }} />
                    <Box>
                      <Typography variant="body2" fontWeight={600}>{pr.exercise_name}</Typography>
                      {pr.pr_type && (
                        <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'capitalize' }}>
                          {pr.pr_type.replace(/_/g, ' ')}
                        </Typography>
                      )}
                    </Box>
                  </Box>
                  <Box sx={{ textAlign: 'right' }}>
                    <Typography variant="subtitle2" fontWeight={700} color={pr.is_all_time ? 'warning.main' : 'primary.main'}>
                      {pr.new_value?.toLocaleString()} {pr.unit}
                    </Typography>
                    {pr.is_all_time && (
                      <Chip label="ALL-TIME" size="small" color="warning" sx={{ height: 18, fontSize: '0.6rem', fontWeight: 700 }} />
                    )}
                  </Box>
                </Box>
              ))}
            </Stack>
          </DetailSection>
        )}

        {/* Exercise List */}
        <DetailSection 
          title="Exercise Details" 
          icon={<FitnessCenterIcon sx={{ fontSize: 18 }} />} 
          color="primary"
        >
          <Stack spacing={1.5}>
            {exercises.map((exercise, idx) => {
              const hasPR = exercise.is_personal_record;
              const isCardio = (exercise.total_distance_meters > 0 || exercise.total_duration_seconds > 0) && exercise.max_weight_used === 0;
              
              return (
                <Paper 
                  key={exercise.exercise_definition_id || idx}
                  variant="outlined"
                  sx={{ 
                    p: 2, 
                    borderRadius: 2, 
                    borderColor: hasPR ? 'warning.main' : 'divider',
                    bgcolor: hasPR ? alpha(theme.palette.warning.main, 0.05) : 'transparent',
                    cursor: exercise.exercise_definition_id ? 'pointer' : 'default',
                    '&:hover': exercise.exercise_definition_id ? { boxShadow: theme.shadows[2] } : {},
                  }}
                  onClick={() => exercise.exercise_definition_id && handleViewExercise(exercise.exercise_definition_id)}
                >
                  <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', mb: 1.5 }}>
                    <Box>
                      <Typography variant="subtitle1" fontWeight={600} color={exercise.exercise_definition_id ? 'primary.main' : 'text.primary'}>
                        {exercise.activity_name}
                      </Typography>
                      {exercise.primary_muscles?.length > 0 && (
                        <Typography variant="caption" color="text.secondary">
                          {exercise.primary_muscles.slice(0, 3).join(', ')}
                        </Typography>
                      )}
                    </Box>
                    {hasPR && (
                      <Chip icon={<EmojiEventsIcon />} label={exercise.is_all_time_pr ? 'ALL-TIME' : 'PR'} color="warning" size="small" sx={{ fontWeight: 600 }} />
                    )}
                  </Box>
                  
                  <Grid container spacing={2}>
                    {exercise.total_sets_count > 0 && (
                      <Grid item xs={3}>
                        <Typography variant="caption" color="text.secondary">Sets</Typography>
                        <Typography variant="h6" fontWeight={700}>{exercise.working_sets_count || exercise.total_sets_count}</Typography>
                      </Grid>
                    )}
                    
                    {isCardio ? (
                       <>
                        {exercise.total_distance_meters > 0 && (
                          <Grid item xs={3}>
                            <Typography variant="caption" color="text.secondary">Distance</Typography>
                            <Typography variant="h6" fontWeight={700}>
                              {exercise.total_distance_meters > 1000 
                                ? `${(exercise.total_distance_meters / 1000).toFixed(2)} km` 
                                : `${exercise.total_distance_meters} m`}
                            </Typography>
                          </Grid>
                        )}
                        {exercise.total_duration_seconds > 0 && (
                          <Grid item xs={3}>
                            <Typography variant="caption" color="text.secondary">Duration</Typography>
                            <Typography variant="h6" fontWeight={700}>
                              {Math.round(exercise.total_duration_seconds / 60)} min
                            </Typography>
                          </Grid>
                        )}
                       </>
                    ) : (
                       <>
                        {exercise.total_reps > 0 && (
                          <Grid item xs={3}>
                            <Typography variant="caption" color="text.secondary">Reps</Typography>
                            <Typography variant="h6" fontWeight={700}>{exercise.total_reps}</Typography>
                          </Grid>
                        )}
                        {exercise.max_weight_used > 0 && (
                          <Grid item xs={3}>
                            <Typography variant="caption" color="text.secondary">Max Weight</Typography>
                            <Typography variant="h6" fontWeight={700}>{exercise.max_weight_used} {exercise.weight_unit || 'lbs'}</Typography>
                          </Grid>
                        )}
                        {exercise.total_volume_load > 0 && (
                          <Grid item xs={3}>
                            <Typography variant="caption" color="text.secondary">Volume</Typography>
                            <Typography variant="h6" fontWeight={700}>{exercise.total_volume_load.toLocaleString()}</Typography>
                          </Grid>
                        )}
                       </>
                    )}
                  </Grid>

                  {exercise.vs_last_session && (
                    <Box sx={{ display: 'flex', gap: 1, mt: 1.5 }}>
                      <TrendIndicator direction={exercise.vs_last_session.overall_direction} />
                      {exercise.vs_last_session.volume_change_percent !== 0 && !isCardio && (
                        <DeltaChip value={exercise.vs_last_session.volume_change_percent} label="Vol" />
                      )}
                      {exercise.vs_last_session.max_weight_change_percent !== 0 && !isCardio && (
                        <DeltaChip value={exercise.vs_last_session.max_weight_change_percent} label="Wt" />
                      )}
                    </Box>
                  )}
                </Paper>
              );
            })}
          </Stack>
        </DetailSection>
      </Box>
    </Fade>
  );
};

ExercisesAndPRsDetail.propTypes = {
  prSummary: PropTypes.object,
  exerciseAnalyses: PropTypes.array,
  activityReports: PropTypes.array,
  onBack: PropTypes.func.isRequired,
};

export default ExercisesAndPRsDetail;